import { useEffect } from "react";
import zipcodes from 'zipcodes';
import * as Yup from 'yup';
import { state } from '../../../const';
import { map, mapKeys, remove } from 'lodash';
import { Alert } from '../../Alert';
import { decamelize } from "humps";
import * as R from 'ramda'
import { errorMessages } from "../../../ErrorConfig/errorConfig";
import { sendAnalyticsError } from "../../../libs/analytics";
import { kindId } from '../../FormContent/DiagnosisForm/validation';

export const useOutsideClick = (ref, callback) => {
  const handleClick = e => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback();
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  });
};

export const getZipCode = (value) => {
  const zipInfo = zipcodes.lookup(value);

  if (zipInfo !== undefined) {
    const stateName = state[zipInfo.state];
    return {
      ...zipInfo,
      state: `${zipInfo.state}, ${stateName}`
    };
  }
}

export const updateList = (newList, currentList) => {
  return map(
    Object.assign(
      ...[newList, [currentList]].map((item) => mapKeys(item, (v) => v.id))
    )
  );
};

export const removeFromList = (list, key, value) => {
  return remove(list, (item) => item[key] !== value);
};

export const showAlertOnValidations = ({
  isValidating,
  isSubmitting,
  isValid,
  setValidationCount,
  submitCount,
  validationCount,
  toast,
  errors,
  closeRef,
  errorDischarging,
  onFacilityOpen,
  componentName,
}) => {
  if (isValidating === true && isSubmitting === true) {
    setValidationCount(submitCount + 1)
  }

  if (isValidating === false && isSubmitting === false && !isValid && validationCount > submitCount) {
    if (closeRef.current) return;
    setValidationCount(submitCount);

    if(errorDischarging){
      onFacilityOpen();
    }

    sendAnalyticsError(componentName, errorMessages.informationMissing);

    if(errors && errors['QID2'] === 'Invalid Date') return;

    return Alert(
      toast,
      errorMessages.canNotContinue,
       errorMessages.informationMissing,
      'error',
      null,
      closeRef
    );
  }
}

export const validateFormat = (val, length) => {
  return val && val.replace(/[^a-zA-Z0-9]/g, '').length === length
}

export const defaultValue = (options, value) => options ? options.find(option => option.value === value) : '';

export const payloadBuilder = (values) => {
  const payload = {case: {}}
  Object.keys(values).map(key => {
    if (key !== 'formSwitched' && values[key] !== '') {
      payload.case[decamelize(key)] = values[key];
    }
  });

  return payload;
}

export const formatEditOptions = (data) => {
  return map(data, ({ displayText, internalId, disabled }, idx) => {
    return { label: `${displayText}`, value: internalId, contentId: idx, disabled};
  });
}

export const formatDate = (date) => {
  if (!date) return;
  if (date.indexOf("-") === -1) return date;
  const [year, month, day] = date.split("-");
  return month + "/" + day + "/" + year;
};

export const getPhysiciansByType = (data, physicianType) => {
  const foundProviders = data.find(physician => physician.providerAssociation === physicianType);
  return foundProviders ? [foundProviders] : []
}

export const mapNewDiagnosisData = (data) => {
  return { diagnosis: data.map(({ diagnosisCode, diagnosisType, gdfId, diagnosisDescription }) => {
    return {
      diagnosisCode,
      diagnosisType,
      gdfId,
      diagnosisDescription
    }
  })};
}

export const formatDiagnoses = (data) => {
  return data.map((item) => {
    return {
      ...item,
      active: 'Y',
      externalId: item.gdfId,
      icdCode: item.diagnosisCode,
      id: '1234',
      internalId: item.gdfId,
      kind: kindId(item.diagnosisType),
      longDescription: item.diagnosisDescription,
      sortOrder: '12340',
      version: '10',
      existing: true,
      diagnosisId: item.gdfId
    }
  });
}

export const formatNumber = (number) => {
  return number
    ? number.replace(/\D+/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
    : "-"
};

export const capitalizeDescription = (string) => {
  return string && string.replace(/\(([^)]+)\)/, function(match) {
    return match.toUpperCase()
  });
}

const sortedArray = (array) => {
  return array.sort(function(a, b){
    const compareDate = new Date(b['startDate']) - new Date(a['startDate']);
    const compareRev = a['revCode'].localeCompare(b['revCode']);

    return compareRev || compareDate;
  });
};

export const sortDefaultEditArray = (array) => {
  const progressArray = [], completeArray = [];
  for (let i = 0; i < array.length; i++) {
    if (array[i]['requestStatus'] === 'In Progress') {
      progressArray.push(array[i]);
    }
    if (array[i]['requestStatus'] === 'Complete') {
      completeArray.push(array[i]);
    }
  }
  return [...sortedArray(progressArray), ...sortedArray(completeArray)]
}

export const sortDefaultArray = (array, sortBy) => {
  return array.sort(function (a, b) {
    if (a[sortBy] < b[sortBy]) {
      return -1;
    }
    if (a[sortBy] > b[sortBy]) {
      return 1;
    }
    return 0;
  });
};

export const npiValidationSchema = () => Yup.string().matches(/^[0-9]+?$/, 'format').min(10, 'format').max(10, 'format')

export const avoidNotNumberKey = (e) =>
  ((e.which !== 8 && e.which !== 0 && e.which < 48) || e.which > 57) &&
  e.preventDefault();


export const titleCaseToKebabCase = R.compose(
  R.replace(/ /g,"-"),
  R.replace(/\?|,|\(|\)|\.|\//g,""),
  R.toLower,
)

export const validateNpiInput = ({value}) => {
  const pattern = /^[0-9]+$/;
  return !value || pattern.test(value);
}

export const updateParamAndReload = (key, value) => {
  const url = new URL(window.location.href);
  url.searchParams.set(key, value);
  window.location.href = url.toString();
}